import i18n from 'i18next';
import { lazy } from 'react';

import { flags } from 'utils/features';

import type { WidgetDefinition, WidgetId } from './types';

/**
 * The catalog of available dashboard widgets. The four launch widgets (Critical
 * Damages, Portfolio Capacity, Repair Task Status, Damage Detection Trend) ship
 * visible by default; the legacy Home widgets (Fleet Map, Attention Required,
 * Upcoming Tasks) are catalog-only, added on demand. Each component is lazily
 * loaded so widgets not in a user's layout never enter the bundle critical path or
 * fire their queries. `dataState: 'preview'` marks sample-data widgets, tagged
 * "Sample" in the catalog. `selfChrome: true` marks
 * widgets that render their own card/table title, so the frame hides its header in
 * view mode.
 */
export const WIDGET_REGISTRY: Record<WidgetId, WidgetDefinition> = {
  'critical-damages': {
    id: 'critical-damages',
    title: i18n.t('dashboard.widgets.criticalDamages.title', 'Critical Damages'),
    description: i18n.t(
      'dashboard.widgets.criticalDamages.description',
      'Critical Damages from the latest inspection per Turbine, split by repair-task coverage.'
    ),
    category: 'damage',
    dataState: 'real',
    component: lazy(() => import('./widgets/CriticalDamagesWidget')),
    defaultLayout: { w: 4, h: 3, minW: 3, minH: 2 },
    requiredFeatures: [],
    selfChrome: false,
  },
  'repair-task-status': {
    id: 'repair-task-status',
    title: i18n.t('dashboard.widgets.repairTaskStatus.title', 'Repair Task Status'),
    description: i18n.t(
      'dashboard.widgets.repairTaskStatus.description',
      'Based on all non-archived Repair Tasks'
    ),
    category: 'tasks',
    dataState: 'real',
    component: lazy(() => import('./widgets/RepairTaskStatusWidget')),
    defaultLayout: { w: 4, h: 3, minW: 3, minH: 3 },
    requiredFeatures: [],
    selfChrome: false,
  },
  'damage-detection-trend': {
    id: 'damage-detection-trend',
    title: i18n.t('dashboard.widgets.damageDetectionTrend.title', 'Damage Detection Trend'),
    description: i18n.t(
      'dashboard.widgets.damageDetectionTrend.description',
      'Based on the severity of the latest observation per Damage within each year'
    ),
    category: 'damage',
    dataState: 'real',
    component: lazy(() => import('./widgets/DamageDetectionTrendWidget')),
    defaultLayout: { w: 12, h: 6, minW: 4, minH: 4 },
    requiredFeatures: [],
    selfChrome: false,
  },
  'predicted-propagation': {
    id: 'predicted-propagation',
    title: i18n.t('dashboard.widgets.predictedPropagation.title', 'Predicted Propagation'),
    description: i18n.t(
      'dashboard.widgets.predictedPropagation.description',
      'Propagation prediction applied to Severity 3+ Crack, Delamination, and Bondline Damages.'
    ),
    category: 'damage',
    dataState: 'real',
    component: lazy(() => import('./widgets/PredictedPropagationWidget')),
    defaultLayout: { w: 12, h: 6, minW: 4, minH: 4 },
    requiredFeatures: [flags.RISK_MANAGEMENT],
    showWhenUnavailable: true,
    selfChrome: false,
  },
  'portfolio-capacity': {
    id: 'portfolio-capacity',
    title: i18n.t('dashboard.widgets.portfolioCapacity.title', 'Portfolio Capacity'),
    description: i18n.t(
      'dashboard.widgets.portfolioCapacity.description',
      'Total fleet capacity and Turbine count.'
    ),
    category: 'fleet',
    dataState: 'real',
    component: lazy(() => import('./widgets/PortfolioCapacityWidget')),
    defaultLayout: { w: 4, h: 3, minW: 2, minH: 2 },
    requiredFeatures: [],
    selfChrome: false,
  },
  'fleet-map': {
    id: 'fleet-map',
    title: i18n.t('dashboard.widgets.fleetMap.title', 'Fleet Map'),
    description: i18n.t(
      'dashboard.widgets.fleetMap.description',
      'Map of every Turbine in your fleet.'
    ),
    category: 'fleet',
    dataState: 'real',
    component: lazy(() => import('./widgets/FleetMapWidget')),
    defaultLayout: { w: 8, h: 5, minW: 4, minH: 4 },
    requiredFeatures: [],
    selfChrome: false,
  },
  'attention-required': {
    id: 'attention-required',
    title: i18n.t('dashboard.widgets.attentionRequired.title', 'Attention Required'),
    description: i18n.t(
      'dashboard.widgets.attentionRequired.description',
      'Critical Damages that need triage or a Work Order.'
    ),
    category: 'damage',
    dataState: 'real',
    component: lazy(() => import('./widgets/AttentionRequiredWidget')),
    defaultLayout: { w: 12, h: 6, minW: 4, minH: 4 },
    requiredFeatures: [],
    selfChrome: true,
  },
  'upcoming-tasks': {
    id: 'upcoming-tasks',
    title: i18n.t('dashboard.widgets.upcomingTasks.title', 'Upcoming Tasks'),
    description: i18n.t(
      'dashboard.widgets.upcomingTasks.description',
      'Tasks due within the next month.'
    ),
    category: 'tasks',
    dataState: 'real',
    component: lazy(() => import('./widgets/UpcomingTasksWidget')),
    defaultLayout: { w: 12, h: 6, minW: 4, minH: 4 },
    requiredFeatures: [],
    selfChrome: true,
  },
  'defect-patterns': {
    id: 'defect-patterns',
    title: i18n.t('dashboard.widgets.defectPatterns.title', 'Defect Patterns'),
    description: i18n.t(
      'dashboard.widgets.defectPatterns.description',
      'Known Blade damage zones across your fleet, with Damage counts and estimated liability per zone.'
    ),
    category: 'damage',
    dataState: 'real',
    component: lazy(() => import('./widgets/DefectPatternsWidget')),
    defaultLayout: { w: 6, h: 6, minW: 4, minH: 6 },
    requiredFeatures: ['RISK_MANAGEMENT'],
    selfChrome: false,
  },
};

export const ALL_WIDGET_IDS = Object.keys(WIDGET_REGISTRY) as WidgetId[];

export function getWidgetDefinition(id: WidgetId): WidgetDefinition | undefined {
  return WIDGET_REGISTRY[id];
}

export function isKnownWidgetId(id: string): id is WidgetId {
  return Object.prototype.hasOwnProperty.call(WIDGET_REGISTRY, id);
}
